import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Alert, Badge, Card, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { canSeeAll, formatDate, type HireListItem, PEOPLE_STATUSES, type PeopleScope, type SeparationListItem, statusName, statusTone } from './data';
import { PeopleNav } from './PeopleNav';

type Kind = 'altas' | 'bajas';
type Item = HireListItem | SeparationListItem;

/** Altas o bajas (guía 7.5): las que pedí, las que esperan mi decisión (RR. HH.) y, con usuarios.ver, todas. */
export function PeopleRequestsPage({ kind }: { kind: Kind }) {
  const me = useMe();
  const scopes: PeopleScope[] = ['propias', 'por_aprobar', ...(canSeeAll(me) ? (['todas'] as const) : [])];
  const [params, setParams] = useSearchParams();
  const requested = params.get('alcance') as PeopleScope | null;
  const scope: PeopleScope = requested && scopes.includes(requested) ? requested : 'propias';
  const status = (PEOPLE_STATUSES as readonly string[]).includes(params.get('estado') ?? '') ? params.get('estado')! : '';

  const query = new URLSearchParams({ alcance: scope });
  if (status) query.set('estado', status);
  const list = useQuery({
    queryKey: ['usuarios', kind, 'lista', scope, status],
    queryFn: () => api<Item[]>(`/api/usuarios/${kind}?${query}`),
    placeholderData: (prev) => prev,
  });
  const pending = useQuery({
    queryKey: ['usuarios', kind, 'lista', 'por_aprobar', 'pendiente'],
    queryFn: () => api<Item[]>(`/api/usuarios/${kind}?alcance=por_aprobar&estado=pendiente`),
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t(`us.${kind}_titulo` as MessageKey)}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t(`us.${kind}_descripcion` as MessageKey)}</p>
        </div>
        <Link to={`/usuarios/${kind}/nueva`} className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
          {t(`us.${kind}_nueva` as MessageKey)}
        </Link>
      </div>

      <PeopleNav />

      <div role="tablist" aria-label={t('us.alcance')} className="flex gap-1 border-b border-slate-200">
        {scopes.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={s === scope}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${s === scope ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            onClick={() => update({ alcance: s })}
          >
            {t(`us.alcance_${s}` as MessageKey)}
            {s === 'por_aprobar' && !!pending.data?.length && (
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900">{pending.data.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="w-56">
        <Select id="us-estado" label={t('us.estado')} value={status} onChange={(e) => update({ estado: e.target.value || null })}>
          <option value="">{t('us.estado_todos')}</option>
          {PEOPLE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusName(s)}
            </option>
          ))}
        </Select>
      </div>

      <Card className="p-0">
        {!list.data ? (
          <div className="p-5">{list.error ? <Alert>{errorMessage(list.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : list.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{scope === 'por_aprobar' ? t('us.vacio_por_aprobar') : t('us.vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('us.col_persona')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.estado')}</th>
                  <th className="px-4 py-2 font-medium">{kind === 'altas' ? t('us.col_ingreso') : t('us.col_ultimo_dia')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.col_solicitante')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.col_fecha')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/usuarios/${kind}/${r.number}`} className="font-medium text-slate-900 hover:underline">
                        <span className="mr-2 font-mono text-xs text-slate-500">{r.code}</span>
                        {r.fullName ?? '—'}
                      </Link>
                      <span className="block text-xs text-slate-500">{r.organization}</span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={statusTone(r.status)}>{statusName(r.status)}</Badge>
                      {'applied' in r && r.applied && <span className="ml-1.5 text-xs text-slate-500">{t('us.baja_aplicada')}</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{formatDate('startDate' in r ? r.startDate : r.lastDay)}</td>
                    <td className="px-4 py-2 text-slate-700">{r.requester ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-600">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
